import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { ProductResultsContext } from './ProductResultsContext';
import { StandardDimension } from './Dimensions/StandardDimension';
import { LinkDimension } from './Dimensions/LinkDimension';
import { RatingStarDimension } from './Dimensions/RatingStarDimension';
import { DimensionsPlaceholder, DimensionsPlaceholderMobile } from './Dimensions/DimensionsPlaceholder';
import { VisualDimension } from './Dimensions/VisualDimension';
import { NumericRangeDimension } from './Dimensions/NumericRangeDimension';
import { ColorSwatchDimension } from './Dimensions/ColorSwatchDimension';
import { AvailabilityDimension } from './Dimensions/AvailabilityDimension';
import {
  sortDimensions, getIsMajorAppliance, getRefinementUrlWithSort, getRefinementIndex, onMultiStoreSelectionChange, hasFitCompatibility
} from '../product-results-helpers';
import { getFirstNonEmptyObject } from '../utils/searchHelpers';
import { getAvailabilityRefinementLabel } from '../utils/fulfillment-helpers';
import '../styles/results-dimensions.scss';

const ResultsDimensions = ({
  onDimensionsChange,
  enableMultiStore,
  onDisplay,
  onDisplayChange,
  shiftCategoryDimension,
  showOnlyGetItFast,
  isGetItFastOnPlp,
  viewPrimaryFilters,
  dataProp,
  isPlpSpeedEnabled
}) => {
  const { data: dataFromContext, loading } = useContext(ProductResultsContext);
  const data = getFirstNonEmptyObject(dataFromContext, dataProp);
  const { nearByStores, store } = useContext(ExperienceContext);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const isMajorAppliance = getIsMajorAppliance(data);
  const hideMAFulfillmentFilters = useConfigService('fs-prop:hideMAFulfillmentFilters') || false;

  const { taxonomy, dimensions, appliedDimensions, searchReport, metadata } = data?.searchModel || {};
  const { keyword } = searchReport || {};
  const pageType = keyword ? 'search' : 'browse';
  const shouldShowDeliverySpeedTile = isMajorAppliance && pageType === 'browse';
  const hideFulfillmentFilters = hideMAFulfillmentFilters && isMajorAppliance && pageType === 'browse';
  if (!dimensions) {
    return !isMobile
      ? <DimensionsPlaceholder isGetItFastOnPlp={isGetItFastOnPlp} />
      : <DimensionsPlaceholderMobile />;
  }
  if (dimensions.length === 0) return null;

  const { breadCrumbs = [] } = taxonomy || {};

  const sortedDimensions = sortDimensions({ dimensions, searchReport, metadata, shiftCategoryDimension });

  const onRefinementChange = ({ refinement, dimension, onDisplay: displayValue }) => {
    document.body.classList.add('results-dimensions--fade');
    setTimeout(() => {
      if (onDimensionsChange) {
        const refinementUrl = getRefinementUrlWithSort({ dimension, metadata, refinement, searchReport });
        const { label: dimensionName } = dimension;
        const updatedRefinement = {
          ...refinement,
          url: refinementUrl,
          redirectUrl: dimensionName.toUpperCase() === 'CATEGORY' ? refinementUrl : null,
          dimensionName,
          refinementIndex: getRefinementIndex(refinement, dimension)
        };
        // Only trigger the event when the filter is a primary filter and the dimension is "Get It Fast"
        //determining the added value of current action by looking at the refinement.selected value. 
        // If the refinement is already selected, then the user is deselecting it, so the added value is null. 
        // If the refinement is not selected, then the user is selecting it, so the added value is the analytics payload. 
        if (viewPrimaryFilters && dimensionName === 'Get It Fast') {
          const deselectedTheRefinement = !!refinement?.selected;
          const analyticsPayload = {
            type: dimensionName,
            value: refinement.label,
            label: getAvailabilityRefinementLabel(refinement, metadata?.isStoreDisplay),
            filterType: 'primary'
          };
          const added = deselectedTheRefinement ? [] : [analyticsPayload];
          //  find any previously applied refinements got deselcted , so we can pass that filters as removed
          // because  user can select a new refinement without deselecting  currently selected value,
          const appliedGetItFast = appliedDimensions?.find((dim) => dim.label === 'Get It Fast');
          const implicitlyRemoved = !deselectedTheRefinement
            ? (appliedGetItFast?.refinements || [])
              .filter((ref) => ref.refinementKey !== refinement.refinementKey)
              .map((ref) => ({
                type: dimensionName,
                value: ref.label,
                label: getAvailabilityRefinementLabel(ref, metadata?.isStoreDisplay),
                filterType: 'primary'
              }))
            : [];
          const removed = deselectedTheRefinement ? [analyticsPayload] : implicitlyRemoved;
          window.LIFE_CYCLE_EVENT_BUS?.trigger('plp-filters.click', { added, removed });
        }

        onDimensionsChange({ refinement: updatedRefinement, onDisplay: displayValue });
      }
      document.body.classList.remove('results-dimensions--fade');
    }, 0);
  };

  const dimensionsClass = classNames('results-dimensions', {
    'results-dimensions--GIF-plp': isGetItFastOnPlp,
    'results-dimensions--fade': loading,
    'results-dimensions--mini': isMobile,
  });

  const multiStoreSelection = nearByStores?.length ? [store?.storeId, ...nearByStores] : [];

  const onDisplayData = {
    onDisplay,
    onDisplayChange,
    isStoreDisplay: data?.searchModel?.metadata?.isStoreDisplay || null,
    itemCount: data?.searchModel?.metadata?.productCount?.inStore
  };

  return (
    <div className={dimensionsClass} data-component="ResultsDimensions">
      {sortedDimensions.map((dimension) => {
        const {
          dimensionId, label, collapse, isVisualDimension, isNumericFilter, isColorSwatch,
        } = dimension;
        const hasFilter = ['BRAND', 'COLOR FAMILY', 'PATTERN TYPE', 'THEME', 'FEATURED KEYWORDS',
          'CUSTOMER SEARCHED KEYWORDS', 'POPULAR KEYWORDS'].indexOf(label.toUpperCase()) > -1;
        const hasFitCompatibilityEnabled = hasFitCompatibility(label);

        if ((showOnlyGetItFast && label.toUpperCase() === 'GET IT FAST') || label.toUpperCase() === 'GET IT FAST') {
          if (viewPrimaryFilters && isGetItFastOnPlp) {
            return (
              <AvailabilityDimension
                collapsed={collapse}
                key={dimensionId}
                dimension={dimension}
                onChange={onRefinementChange}
                enableMultiStore={enableMultiStore}
                onMultiStoreSelectionChange={onMultiStoreSelectionChange}
                multiStoreSelection={multiStoreSelection}
                onDisplayData={onDisplayData}
                isMobile={isMobile}
                isGetItFastOnPlp={isGetItFastOnPlp}
                shouldShowDeliverySpeedTile={shouldShowDeliverySpeedTile}
                hideFulfillmentFilters={hideFulfillmentFilters}
              />
            );
          }
          return (
            <StandardDimension
              collapsed={collapse}
              key={dimensionId}
              dimension={dimension}
              fitCompatibility={hasFitCompatibilityEnabled}
              onChange={onRefinementChange}
              enableMultiStore={enableMultiStore}
              onMultiStoreSelectionChange={onMultiStoreSelectionChange}
              multiStoreSelection={multiStoreSelection}
              onDisplayData={onDisplayData}
              parentID="leftNav"
            />
          );
        }

        if (showOnlyGetItFast) {
          return null;
        }

        if (label.toUpperCase() === 'CATEGORY') {
          return (
            <LinkDimension
              collapsed={collapse}
              key={dimensionId}
              dimension={dimension}
              breadCrumbs={breadCrumbs}
              onChange={onRefinementChange}
            />
          );
        }
        if (label.toUpperCase() === 'REVIEW RATING') {
          return (
            <RatingStarDimension
              collapsed={collapse}
              key={dimensionId}
              dimension={dimension}
              onChange={onRefinementChange}
            />
          );
        }

        if (isVisualDimension) {
          return (
            <VisualDimension
              key={dimensionId}
              collapsed={collapse}
              onChange={onRefinementChange}
              dimension={dimension}
            />
          );
        }

        if (isNumericFilter) {
          return (
            <NumericRangeDimension
              collapsed={collapse}
              dimension={dimension}
              enableMultiStore={enableMultiStore}
              filter
              key={dimensionId}
              metadata={metadata}
              multiStoreSelection={multiStoreSelection}
              onChange={onRefinementChange}
              onDisplayData={onDisplayData}
              onMultiStoreSelectionChange={onMultiStoreSelectionChange}
            />
          );
        }

        if (isColorSwatch) {
          return (
            <ColorSwatchDimension
              collapsed={collapse}
              dimension={dimension}
              key={dimensionId}
              onChange={onRefinementChange}
            />
          );
        }

        if (hasFilter) {
          return (
            <StandardDimension
              collapsed={collapse}
              filter
              key={dimensionId}
              fitCompatibility={hasFitCompatibilityEnabled}
              dimension={dimension}
              onChange={onRefinementChange}
              enableMultiStore={enableMultiStore}
              onMultiStoreSelectionChange={onMultiStoreSelectionChange}
              multiStoreSelection={multiStoreSelection}
              onDisplayData={onDisplayData}
            />
          );
        }
        return (
          <StandardDimension
            collapsed={collapse}
            priceRange={label.toUpperCase() === 'PRICE'}
            key={dimensionId}
            fitCompatibility={hasFitCompatibilityEnabled}
            dimension={dimension}
            onChange={onRefinementChange}
            enableMultiStore={enableMultiStore}
            onMultiStoreSelectionChange={onMultiStoreSelectionChange}
            multiStoreSelection={multiStoreSelection}
            onDisplayData={onDisplayData}
            isNumericFilter={isNumericFilter}
            metadata={metadata}
          />
        );

      })}
    </div>
  );
};

ResultsDimensions.propTypes = {
  onDimensionsChange: PropTypes.func.isRequired,
  enableMultiStore: PropTypes.bool,
  onDisplay: PropTypes.string,
  onDisplayChange: PropTypes.func,
  shiftCategoryDimension: PropTypes.bool,
  showOnlyGetItFast: PropTypes.bool,
  isGetItFastOnPlp: PropTypes.bool,
  viewPrimaryFilters: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  dataProp: PropTypes.object,
  isPlpSpeedEnabled: PropTypes.bool
};

ResultsDimensions.defaultProps = {
  enableMultiStore: false,
  onDisplay: 'false',
  onDisplayChange: null,
  shiftCategoryDimension: false,
  showOnlyGetItFast: false,
  isGetItFastOnPlp: false,
  viewPrimaryFilters: false,
  dataProp: null,
  isPlpSpeedEnabled: false
};

ResultsDimensions.displayName = 'Dimensions';

ResultsDimensions.dataModel = extend(StandardDimension);

export { ResultsDimensions };
